import { FormEvent, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column as TableColumn } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Message } from 'primereact/message'
import { PermissionSuffix } from '../../_generated/api/user-management'
import ServiceWindow from '../../models/ServiceWindow'
import MenuBuilder from '../../tools/MenuBuilder'
import {
    MAX_SERVICE_CONNECTIONS,
    SERVICE_INTERVALS,
    validServiceDates,
} from '../../tools/service-window'
import t from '../../tools/t'
import ContextHeader from '../common/ContextHeader'
import ServiceWindowStatus from '../common/ServiceWindowStatus'
import { Chip } from '../common/pure/chips'
import DateDisplay from '../common/pure/DateDisplay'
import { Column, Row } from '../common/pure/FlexContainer'
import Heading from '../common/pure/Heading'
import { CalendarField, NumberField, TextField } from '../common/pure/input-fields'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import PrimeIcon, { pi } from '../common/pure/PrimeIcon'
import { useServiceWindowContext } from '../contexts/ServiceWindowContext'
import { useStructureContext } from '../contexts/StructureContext'
import { useUserContext } from '../contexts/UserContext'
import ServiceWindowCompleteDialog from '../dialogs/ServiceWindowCompleteDialog'
import ServiceWindowConnectionsDialog from '../dialogs/ServiceWindowConnectionsDialog'
import TablePagination from '../table/TablePagination'

export default function ServiceWindowScreen() {
    const { serviceWindowId } = useParams()
    const { windows, loading, loadError, selected, selectedLoading, selectedError, refresh } =
        useServiceWindowContext()
    const { can } = useUserContext()
    const canWrite = can(PermissionSuffix.StructureWrite)
    const [completing, setCompleting] = useState<ServiceWindow>()
    const navigate = useNavigate()

    return (
        <LayoutColumnContainer className="service-window-screen">
            <LayoutRowContainer grow={false}>
                <ContentColumn columns={3}>
                    <ContextHeader
                        menu={new MenuBuilder()
                            .add({
                                label: t('service_window_refresh'),
                                icon: pi('refresh'),
                                command: refresh,
                            })
                            .toArray()}
                    >
                        <Heading standalone size={2}>
                            {t('service_window_title')}
                        </Heading>
                    </ContextHeader>
                    {serviceWindowId && (
                        <Link to="/service-window">{t('service_window_back')}</Link>
                    )}
                    {serviceWindowId && !selected ? (
                        <Card>
                            <Message
                                severity={selectedError ? 'error' : 'info'}
                                text={selectedError?.message ?? t('service_window_loading')}
                            />
                            {!!selectedError && (
                                <Button text label={t('service_window_retry')} onClick={refresh} />
                            )}
                        </Card>
                    ) : (
                        <ServiceWindowForm
                            key={selected ? `${selected.id}:${selected.data.revision}` : 'new'}
                            window={selected}
                        />
                    )}
                    {selected && (
                        <Card title={t('service_window_details')}>
                            <Row
                                gap={1}
                                alignment="center"
                                arrangement="spaced"
                                className="service-window-wrap"
                            >
                                <Column gap={0.5}>
                                    <ServiceWindowStatus status={selected.data.status} />
                                    {selected.data.nextService && (
                                        <span>
                                            {t('service_window_next')}:{' '}
                                            <DateDisplay
                                                value={selected.data.nextService.scheduledFor}
                                                target={selected.id}
                                            />
                                        </span>
                                    )}
                                    {selected.data.lastCompletion && (
                                        <span>
                                            {t('service_window_last_completed')}:{' '}
                                            <DateDisplay
                                                value={selected.data.lastCompletion.completedAt}
                                                target={`completed-${selected.id}`}
                                            />
                                        </span>
                                    )}
                                    {selected.data.isTerminal && (
                                        <span>{t('service_window_terminal')}</span>
                                    )}
                                </Column>
                                {canWrite && selected.canComplete && (
                                    <Button
                                        label={t('service_window_serviced')}
                                        icon={pi('check')}
                                        disabled={selectedLoading}
                                        onClick={() => setCompleting(selected)}
                                    />
                                )}
                            </Row>
                        </Card>
                    )}
                </ContentColumn>
            </LayoutRowContainer>
            <LayoutRowContainer grow={false}>
                <ContentColumn columns={3}>
                    <Card title={t('service_window_list')}>
                        {loadError && (
                            <Row gap={1}>
                                <Message severity="error" text={loadError.message} />
                                <Button label={t('service_window_retry')} onClick={refresh} />
                            </Row>
                        )}
                        <DataTable
                            dataKey="id"
                            value={windows.items}
                            loading={loading}
                            rowHover
                            emptyMessage={
                                loadError
                                    ? t('service_window_load_failed')
                                    : t('service_window_empty')
                            }
                            onRowClick={(event) =>
                                navigate((event.data as ServiceWindow).referenceLink)
                            }
                        >
                            <TableColumn
                                header={t('service_window_connections')}
                                body={(window: ServiceWindow) => (
                                    <Row gap={1} alignment="center">
                                        <PrimeIcon
                                            name={
                                                window.data.boltConnections.length === 1
                                                    ? 'box'
                                                    : 'folder'
                                            }
                                        />
                                        <Column gap={0.25}>
                                            <Link
                                                to={window.referenceLink}
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <strong>{window.name}</strong>
                                            </Link>
                                            <span className="service-window-muted">
                                                {window.data.boltConnections
                                                    .map((connection) =>
                                                        [
                                                            connection.path.replaceAll(':', ' / '),
                                                            connection.name,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' / ')
                                                    )
                                                    .join(' · ')}
                                            </span>
                                        </Column>
                                    </Row>
                                )}
                            />
                            <TableColumn
                                header={t('service_window_interval')}
                                body={(window: ServiceWindow) => (
                                    <Chip
                                        label={t(
                                            'service_window_days',
                                            String(window.data.repeatDays)
                                        )}
                                        color="#006b86"
                                    />
                                )}
                            />
                            <TableColumn
                                header={t('service_window_next')}
                                body={(window: ServiceWindow) =>
                                    window.data.nextService ? (
                                        <DateDisplay
                                            value={window.data.nextService.scheduledFor}
                                            target={`table-${window.id}`}
                                        />
                                    ) : (
                                        '—'
                                    )
                                }
                            />
                            <TableColumn
                                header={t('common_status')}
                                body={(window: ServiceWindow) => (
                                    <ServiceWindowStatus status={window.data.status} />
                                )}
                            />
                            <TableColumn
                                body={(window: ServiceWindow) => (
                                    <Link
                                        to={window.referenceLink}
                                        onClick={(event) => event.stopPropagation()}
                                        aria-label={`${t('service_window_view')}: ${window.name}`}
                                    >
                                        <PrimeIcon name="angle-right" />
                                    </Link>
                                )}
                            />
                        </DataTable>
                        <TablePagination data={windows} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
            {completing && (
                <ServiceWindowCompleteDialog
                    window={completing}
                    onHide={() => setCompleting(undefined)}
                />
            )}
        </LayoutColumnContainer>
    )
}

function ServiceWindowForm({ window }: { window?: ServiceWindow }) {
    const { can } = useUserContext()
    const { structure } = useStructureContext()
    const { save, saving } = useServiceWindowContext()
    const navigate = useNavigate()
    const readOnly = !can(PermissionSuffix.StructureWrite)
    const [name, setName] = useState(window?.name ?? '')
    const [ids, setIds] = useState(
        window?.data.boltConnections.map((connection) => connection.id) ?? []
    )
    const [repeatDays, setRepeatDays] = useState<number | null>(window?.data.repeatDays ?? 90)
    const [startDate, setStartDate] = useState<Date | null>(
        window ? new Date(window.data.startDate) : new Date(new Date().setHours(0, 0, 0, 0))
    )
    const [endDate, setEndDate] = useState<Date | null>(
        window ? new Date(window.data.endDate) : null
    )
    const [selecting, setSelecting] = useState(false)
    const [changed, setChanged] = useState(false)
    const [failed, setFailed] = useState(false)
    const disabled = readOnly || saving
    const valid =
        name.trim().length > 0 &&
        name.trim().length <= 200 &&
        ids.length > 0 &&
        ids.length <= MAX_SERVICE_CONNECTIONS &&
        Number.isInteger(repeatDays) &&
        (repeatDays ?? 0) > 0 &&
        (repeatDays ?? 0) <= 2147483647 &&
        validServiceDates(startDate, endDate)

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        if (!valid || disabled || !startDate || !endDate || !repeatDays) return
        const result = await save(
            {
                name: name.trim(),
                boltConnectionIds: ids,
                repeatDays,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
            },
            window
        )
        if (result) {
            setChanged(false)
            setFailed(false)
            navigate(result.referenceLink)
        } else setFailed(true)
    }

    return (
        <Card
            title={window ? window.name : t('service_window_customize')}
            subTitle={t('service_window_intro')}
        >
            <form onSubmit={submit} className="service-window-form">
                <TextField
                    placeholder={t('service_window_name')}
                    aria-label={t('service_window_name')}
                    value={name}
                    maxLength={200}
                    required
                    disabled={disabled}
                    onChange={(event) => {
                        setName(event.target.value)
                        setChanged(true)
                    }}
                />
                <div className="service-window-selection">
                    <span className="service-window-muted">
                        {t('service_window_select_connections')}
                    </span>
                    <Row gap={0.5} alignment="center" className="service-window-wrap">
                        {ids.map((id) => {
                            const entry = structure.findEntry(id)
                            const reference = window?.data.boltConnections.find(
                                (item) => item.id === id
                            )
                            const label = entry?.name ?? reference?.name ?? id
                            const path = entry
                                ? structure
                                      .findEntryAncestors(id)
                                      .map((item) => item.name)
                                      .join(' / ')
                                : [reference?.path, label].filter(Boolean).join(' / ')
                            const unavailable =
                                reference?.isErased || reference?.isArchived || entry?.isArchived
                            return (
                                <span
                                    key={id}
                                    className="service-window-selection__chip"
                                    title={path}
                                >
                                    {reference?.isErased ? (
                                        <span>{label}</span>
                                    ) : (
                                        <Link to={`/connection/${id}`}>{label}</Link>
                                    )}
                                    {unavailable && (
                                        <span>({t('service_window_unavailable')})</span>
                                    )}
                                    {!readOnly && (
                                        <Button
                                            type="button"
                                            text
                                            rounded
                                            size="small"
                                            icon={pi('times')}
                                            disabled={disabled}
                                            aria-label={t(
                                                'service_window_remove_connection',
                                                label
                                            )}
                                            onClick={() => {
                                                setIds(ids.filter((value) => value !== id))
                                                setChanged(true)
                                            }}
                                        />
                                    )}
                                </span>
                            )
                        })}
                        {!readOnly && (
                            <Button
                                type="button"
                                size="small"
                                outlined
                                icon={pi('plus')}
                                label={t('service_window_add')}
                                disabled={disabled}
                                onClick={() => setSelecting(true)}
                            />
                        )}
                        {ids.length === 0 && (
                            <span className="service-window-muted">
                                {t('service_window_select_hint')}
                            </span>
                        )}
                    </Row>
                </div>
                <div className="service-window-form__schedule">
                    <Column gap={0.5}>
                        <span className="service-window-muted">{t('service_window_interval')}</span>
                        <Row gap={0.5} className="service-window-wrap">
                            {SERVICE_INTERVALS.map((days) => (
                                <Button
                                    key={days}
                                    type="button"
                                    size="small"
                                    outlined={repeatDays !== days}
                                    aria-pressed={repeatDays === days}
                                    label={t('service_window_months', String(days / 30))}
                                    title={t('service_window_days', String(days))}
                                    disabled={disabled}
                                    onClick={() => {
                                        setRepeatDays(days)
                                        setChanged(true)
                                    }}
                                />
                            ))}
                        </Row>
                        <NumberField
                            placeholder={t('service_window_interval_days')}
                            aria-label={t('service_window_interval_days')}
                            value={repeatDays}
                            min={1}
                            max={2147483647}
                            disabled={disabled}
                            onValueChange={(event) => {
                                setRepeatDays(event.value ?? null)
                                setChanged(true)
                            }}
                        />
                        <small className="service-window-muted">
                            {t('service_window_interval_help')}
                        </small>
                    </Column>
                    <CalendarField
                        placeholder={t('service_window_start')}
                        aria-label={t('service_window_start')}
                        value={startDate}
                        disabled={disabled}
                        showIcon
                        dateFormat="dd/mm/yy"
                        readOnlyInput
                        onChange={(event) => {
                            setStartDate(event.value as Date | null)
                            setChanged(true)
                        }}
                    />
                    <CalendarField
                        placeholder={t('service_window_end')}
                        aria-label={t('service_window_end')}
                        value={endDate}
                        disabled={disabled}
                        showIcon
                        dateFormat="dd/mm/yy"
                        minDate={startDate ?? undefined}
                        readOnlyInput
                        onChange={(event) => {
                            setEndDate(event.value as Date | null)
                            setChanged(true)
                        }}
                    />
                    {!readOnly && (
                        <Button
                            type="submit"
                            label={t('common_save')}
                            icon={pi('save')}
                            rounded
                            loading={saving}
                            disabled={!valid || !changed || saving}
                        />
                    )}
                </div>
                {startDate && endDate && !validServiceDates(startDate, endDate) && (
                    <Message severity="error" text={t('service_window_invalid_dates')} />
                )}
                {failed && <Message severity="error" text={t('service_window_write_failed')} />}
            </form>
            {selecting && (
                <ServiceWindowConnectionsDialog
                    ids={ids}
                    onHide={() => setSelecting(false)}
                    onApply={(values) => {
                        setIds(values)
                        setChanged(true)
                    }}
                />
            )}
        </Card>
    )
}
